import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { MultiSelect } from "primereact/multiselect";
import { RadioButton } from "primereact/radiobutton";
import { InputNumber } from "primereact/inputnumber";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import styles from "./LayersCard.module.css";
import {
  readableLayer,
  readableLimitOfLiabilityFromLayer,
  sortedLayersFromReport,
} from "./util";

import { SafeReportResponse } from "../Conversion";
import * as React from "react";
import { useState, useMemo, useContext } from "react";
import { ThemeContext } from "../../../themes/Theme";
import {
  HIGHLIGHT_WARNING_LIGHT_MODE,
  HIGHLIGHT_WARNING_DARK_MODE,
  HIGHLIGHT_ERROR_LIGHT_MODE,
  HIGHLIGHT_ERROR_DARK_MODE,
  HIGHLIGHT_USER_OVERRIDE_LIGHT_MODE,
  HIGHLIGHT_USER_OVERRIDE_DARK_MODE,
} from "../dashboard/util";

import { cloneDeep } from "lodash";
import * as client from "../../../client";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import {
  DBPerilType,
  getEffectiveIncludedPerils,
  perilSentenceFromEnabledPerils,
  readableNameFromPeril,
} from "../general-input/util";
import { useCalculatedPremium, useUpdateReport } from "../data";
import { v4 as uuid } from "uuid";
import { formatExactMoneyDollars } from "../Money";

type LayersCardProps = {
  specialtyPropertyInfo: SafeReportResponse;
  title?: string;
};

type SourcePremiumData = {
  aal: number;
  multiple: number | null;
  multipleTooltip: string | null;
  layerPremium: number;
  sharePremium: number;
  warningsTooltip: string | null;
  missingData: boolean;
  hasWarnings: boolean;
  overrideType: "multiple" | "premium" | null;
  /** True when an override update is pending and derived values are not yet recomputed by the backend. */
  stale: boolean;
};

type PremiumPerilRowBase = {
  layer: client.Layer;
  layerId: string;
  layerIndex: number;
  air: SourcePremiumData;
  rms: SourcePremiumData;
  selectedSource: "air" | "rms";
  target: number | null;
  isFirstInGroup: boolean;
};

type PremiumPerilRow = PremiumPerilRowBase &
  (
    | { rowKind: "perilType"; peril: DBPerilType }
    | { rowKind: "perilCode"; peril: DBPerilType; perilCodeName: string }
    | { rowKind: "total"; peril: "Total" }
  );

const PERIL_CODE_DISPLAY_NAMES: Record<string, string> = {
  PWH: "Hurricane - Wind Only",
  PES: "Earthquake - Ground Shake",
  PFL: "Inland Flood",
  PWB: "Wildfire",
  PWW: "Winter Storm",
  PWT: "Severe Thunderstorm",
  AOP: "All Other Perils",
  EBP: "Equipment Breakdown",
};

const perilCodeDisplayName = (code: string): string => {
  return PERIL_CODE_DISPLAY_NAMES[code] ?? code;
};

const PERIL_TYPE_ORDER: DBPerilType[] = [
  "aop",
  "named_storm",
  "earthquake",
  "earth_movement",
  "flood",
  "wildfire",
  "scs",
  "wind_and_hail",
  "equipment_breakdown",
];

function sortedPerilTypes(
  perilTypes: client.LayerPremiumBreakdown["peril_types"]
) {
  return Object.entries(perilTypes)
    .sort(([a], [b]) => {
      const ai = PERIL_TYPE_ORDER.indexOf(a as DBPerilType);
      const bi = PERIL_TYPE_ORDER.indexOf(b as DBPerilType);
      return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi);
    })
    .map(([, perilType]) => perilType);
}

const WARNING_DISPLAY_NAMES: Record<string, string> = {
  na_premium: "N/A premium in rater",
  used_default_construction_code:
    "Used default construction code due to missing data",
  used_default_occupancy_code:
    "Used default occupancy code due to missing data",
};

const EMPTY_SOURCE_DATA: SourcePremiumData = {
  aal: 0,
  multiple: null,
  multipleTooltip: null,
  layerPremium: 0,
  sharePremium: 0,
  warningsTooltip: null,
  missingData: true,
  hasWarnings: false,
  overrideType: null,
  stale: false,
};

export const LayersCard: React.FC<LayersCardProps> = ({
  specialtyPropertyInfo,
  title,
}) => {
  const isLightMode = useContext(ThemeContext).theme === "light";
  const sortedLayers = sortedLayersFromReport(specialtyPropertyInfo);
  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);

  // Layer dialog state — shared between add and edit modes
  const [showLayerDialog, setShowLayerDialog] = useState(false);
  const [editingLayerId, setEditingLayerId] = useState<string | null>(null);
  const [newStartingPoint, setNewStartingPoint] = useState<number | null>(null);
  const [newRange, setNewRange] = useState<number | null>(null);
  const [newParticipation, setNewParticipation] = useState<number | null>(null);

  const isEditing = editingLayerId !== null;

  const resetLayerDialog = () => {
    setShowLayerDialog(false);
    setEditingLayerId(null);
    setNewStartingPoint(null);
    setNewRange(null);
    setNewParticipation(null);
  };

  const openAddDialog = () => {
    setEditingLayerId(null);
    setNewStartingPoint(null);
    setNewRange(null);
    setNewParticipation(null);
    setShowLayerDialog(true);
  };

  const openEditDialog = (layer: client.Layer) => {
    setEditingLayerId(layer.layer_id ?? null);
    setNewStartingPoint(layer.starting_point);
    setNewRange(layer.range);
    setNewParticipation(layer.participation ?? null);
    setShowLayerDialog(true);
  };

  const handleSaveLayer = async () => {
    if (newStartingPoint == null || newRange == null || newRange <= 0) return;
    const newLayers = cloneDeep(specialtyPropertyInfo.report_json.layer_info);

    if (isEditing) {
      const existing = newLayers.find((L) => L.layer_id === editingLayerId);
      if (!existing) return;
      existing.participation = newParticipation;
    } else {
      newLayers.push({
        layer_id: uuid(),
        starting_point: newStartingPoint,
        range: newRange,
        target: null,
        rated: null,
        participation: newParticipation,
        layer_source: "manual",
        is_enabled: true,
        selected_source: null,
        source_overrides: null,
      });
    }

    await updateReport.mutateAsync({ layerInfo: newLayers });
    resetLayerDialog();
  };

  // Live preview of the layer description as user fills out the form
  const layerPreview = useMemo(() => {
    if (newRange == null || newRange <= 0) return null;
    return readableLimitOfLiabilityFromLayer({
      starting_point: newStartingPoint ?? 0,
      range: newRange,
      target: null,
      rated: null,
      participation: newParticipation,
    });
  }, [newStartingPoint, newRange, newParticipation]);

  // Fetch calculated premiums from backend
  const {
    data: premiumData,
    isLoading: firstPremiumLoading,
    isFetching: premiumLoading,
    isError: premiumError,
  } = useCalculatedPremium(specialtyPropertyInfo.report_id);

  // Build a lookup from backend premium data: layer_id -> LayerPremiumBreakdown
  const premiumByLayer = useMemo(() => {
    if (!premiumData?.layers?.length)
      return new Map<string, client.LayerPremiumBreakdown>();
    return new Map<string, client.LayerPremiumBreakdown>(
      premiumData.layers.map((l) => [l.layer_id, l])
    );
  }, [premiumData]);

  // Premium table state — keyed by layer_id
  const layerOptions = sortedLayers.map((l) => ({
    label: `${readableLayer(l)} (${l.layer_source})`,
    value: l.layer_id!,
  }));
  const enabledLayers = specialtyPropertyInfo.report_json.layer_info
    .filter((L) => L.is_enabled)
    .map((L) => L.layer_id);

  // Resolve layer info, preferring optimistic pending mutation data
  const resolvedLayerInfo = useMemo(() => {
    const layers = specialtyPropertyInfo.report_json.layer_info;
    if (!updateReport.isPending || !updateReport.variables?.layerInfo)
      return layers;
    return updateReport.variables.layerInfo;
  }, [
    specialtyPropertyInfo.report_json.layer_info,
    updateReport.isPending,
    updateReport.variables,
  ]);

  const handleSourceOverrideChange = async (
    layerId: string,
    source: "air" | "rms",
    peril: DBPerilType,
    override: client.SourceOverride | undefined
  ) => {
    const layer = specialtyPropertyInfo.report_json.layer_info.find(
      (L) => L.layer_id === layerId
    );
    if (!layer) return;

    const existing = layer.source_overrides?.[source]?.[peril];
    if (override === undefined && existing === undefined) return;
    if (
      override !== undefined &&
      existing !== undefined &&
      existing.multiple === override.multiple &&
      existing.premium === override.premium
    )
      return;

    const newLayers = cloneDeep(specialtyPropertyInfo.report_json.layer_info);
    const newLayer = newLayers.find((L) => L.layer_id === layerId)!;

    newLayer.source_overrides = newLayer.source_overrides ?? {};
    newLayer.source_overrides[source] = newLayer.source_overrides[source] ?? {};

    if (override !== undefined) {
      newLayer.source_overrides[source][peril] = override;
    } else {
      delete newLayer.source_overrides[source][peril];
    }

    await updateReport.mutateAsync({ layerInfo: newLayers });
  };

  const handleSelectedSourceChange = async (
    layerId: string,
    source: "air" | "rms"
  ) => {
    const layer = specialtyPropertyInfo.report_json.layer_info.find(
      (L) => L.layer_id === layerId
    );
    if (!layer || layer.selected_source === source) return;

    const newLayers = cloneDeep(specialtyPropertyInfo.report_json.layer_info);
    const newLayer = newLayers.find((L) => L.layer_id === layerId)!;
    newLayer.selected_source = source;
    await updateReport.mutateAsync({ layerInfo: newLayers });
  };

  const premiumRows = useMemo((): PremiumPerilRow[] => {
    const rows: PremiumPerilRow[] = [];

    for (let li = 0; li < sortedLayers.length; li++) {
      const layer = sortedLayers[li];
      const layerId = layer.layer_id!;
      if (!enabledLayers.includes(layerId)) continue;
      const layerPremium = premiumByLayer.get(layerId);
      const resolvedLayer = resolvedLayerInfo.find(
        (L) => L.layer_id === layerId
      );
      if (!resolvedLayer) continue;
      const selectedSource: "air" | "rms" =
        resolvedLayer.selected_source ?? "rms";

      let isFirst = true;
      if (!layerPremium) continue;
      for (const perilTypeOptionsBySource of sortedPerilTypes(
        layerPremium.peril_types
      )) {
        const firstOption = Object.values(perilTypeOptionsBySource)[0];
        if (!firstOption) continue;
        const perilType = firstOption.peril_type;
        const premiumsBySource: {
          [key: string]: SourcePremiumData | undefined;
        } = {};
        for (const perilTypeOption of Object.values(perilTypeOptionsBySource)) {
          const totalAal = perilTypeOption.total_aal ?? 0;
          const source = perilTypeOption.source;

          // Backend is the source of truth for all derived values.
          // Detect staleness by comparing the resolved (optimistic)
          // override against what the backend premium response reflects.
          const resolvedOv =
            resolvedLayer?.source_overrides?.[source]?.[perilType];
          const backendReflects = {
            multiple:
              perilTypeOption.override_type === "multiple"
                ? perilTypeOption.override_multiple
                : undefined,
            premium:
              perilTypeOption.override_type === "premium"
                ? perilTypeOption.override_premium
                : undefined,
          };
          // Treat null, undefined, and 0 as "no override" for staleness.
          const norm = (v: number | null | undefined) =>
            v != null && v !== 0 ? v : undefined;
          const stale =
            norm(resolvedOv?.multiple) !== norm(backendReflects.multiple) ||
            norm(resolvedOv?.premium) !== norm(backendReflects.premium);

          // Start from backend-computed effective values.
          const hasRealOverride =
            (perilTypeOption.override_type === "multiple" &&
              norm(perilTypeOption.override_multiple) != null) ||
            (perilTypeOption.override_type === "premium" &&
              norm(perilTypeOption.override_premium) != null);
          let overrideType: "multiple" | "premium" | null = hasRealOverride
            ? perilTypeOption.override_type!
            : null;
          let effectiveSharePremium = perilTypeOption.share_effective;
          const effectiveLayerPremium = perilTypeOption.layer_effective;
          let effectiveMultiple = perilTypeOption.multiple_effective;

          if (stale) {
            if (norm(resolvedOv?.multiple) != null) {
              overrideType = "multiple";
              effectiveMultiple = resolvedOv!.multiple!;
            } else if (norm(resolvedOv?.premium) != null) {
              overrideType = "premium";
              effectiveSharePremium = resolvedOv!.premium!;
            } else {
              overrideType = null;
            }
          }

          // Collect warnings across all peril codes
          const missingDataLines: string[] = [];
          const warningLines: string[] = [];
          const multipleTooltipLines: string[] = [];

          for (const perilCode of perilTypeOption.parts) {
            if (perilCode.missing) {
              missingDataLines.push(
                `Missing data for ${perilCodeDisplayName(perilCode.peril_code)}`
              );
            }
            for (const [key, count] of Object.entries(perilCode.warnings)) {
              const label = WARNING_DISPLAY_NAMES[key] ?? key;
              warningLines.push(
                `${label} (${count} location${count !== 1 ? "s" : ""})`
              );
            }
            if (perilCode.multiplier != null) {
              multipleTooltipLines.push(
                `${perilCodeDisplayName(perilCode.peril_code)}: ${perilCode.multiplier.toFixed(1)}x (${perilCode.multiplier_reason})`
              );
            }
          }

          const allWarnings = [...warningLines, ...missingDataLines];
          const warningsTooltip =
            allWarnings.length > 0 ? allWarnings.join("\n") : null;

          const multipleTooltip =
            multipleTooltipLines.length === 0 || !!overrideType
              ? null
              : multipleTooltipLines.length > 1
                ? ["Blended multiple", ...multipleTooltipLines].join("\n")
                : multipleTooltipLines[0];

          premiumsBySource[perilTypeOption.source] = {
            aal: totalAal,
            multiple: effectiveMultiple,
            multipleTooltip,
            layerPremium: effectiveLayerPremium,
            sharePremium: effectiveSharePremium,
            warningsTooltip,
            missingData: missingDataLines.length > 0,
            hasWarnings: warningLines.length > 0,
            overrideType,
            stale,
          };
        }

        rows.push({
          layer: layer,
          layerId: layerId,
          layerIndex: li,
          peril: perilType,
          rowKind: "perilType",
          air: premiumsBySource["air"] ?? EMPTY_SOURCE_DATA,
          rms: premiumsBySource["rms"] ?? EMPTY_SOURCE_DATA,
          selectedSource,
          target: null,
          isFirstInGroup: isFirst,
        });
        isFirst = false;

        // Emit peril-code sub-rows (one per peril code in this peril type)
        const allCodes = new Set<string>();
        for (const opt of Object.values(perilTypeOptionsBySource)) {
          for (const part of opt.parts) {
            allCodes.add(part.peril_code);
          }
        }
        // Only show sub-rows when there are multiple codes — a single code
        // would just duplicate the peril-type row above.
        if (allCodes.size > 1) {
          for (const code of allCodes) {
            const codeBySource: {
              [key: string]: SourcePremiumData | undefined;
            } = {};
            for (const opt of Object.values(perilTypeOptionsBySource)) {
              const part = opt.parts.find((p) => p.peril_code === code);
              if (!part) continue;
              codeBySource[opt.source] = {
                aal: part.aal,
                multiple: part.multiplier,
                multipleTooltip:
                  part.multiplier != null ? part.multiplier_reason : null,
                layerPremium: part.layer_premium,
                sharePremium: part.share_premium,
                warningsTooltip: null,
                missingData: part.missing,
                hasWarnings: Object.keys(part.warnings).length > 0,
                overrideType: null,
                stale: false,
              };
            }
            rows.push({
              layer: layer,
              layerId: layerId,
              layerIndex: li,
              peril: perilType,
              rowKind: "perilCode",
              perilCodeName: perilCodeDisplayName(code),
              air: codeBySource["air"] ?? EMPTY_SOURCE_DATA,
              rms: codeBySource["rms"] ?? EMPTY_SOURCE_DATA,
              selectedSource,
              target: null,
              isFirstInGroup: false,
            });
          }
        }
      }

      // Total row
      const airTotals = layerPremium.totals?.["air"];
      const rmsTotals = layerPremium.totals?.["rms"];

      // Aggregate AAL from peril-type rows for this layer
      const layerRows = rows.filter(
        (r) => r.layerId === layerId && r.rowKind === "perilType"
      );
      const airTotalAal = layerRows.reduce((s, r) => s + r.air.aal, 0);
      const rmsTotalAal = layerRows.reduce((s, r) => s + r.rms.aal, 0);
      const airStale = layerRows.some((r) => r.air.stale);
      const rmsStale = layerRows.some((r) => r.rms.stale);

      rows.push({
        layer: layer,
        layerId: layerId,
        layerIndex: li,
        peril: "Total",
        rowKind: "total",
        air: {
          aal: airTotalAal,
          multiple:
            airTotalAal > 0 ? airTotals.layer_total / airTotalAal : null,
          multipleTooltip: null,
          layerPremium: airTotals.layer_total,
          sharePremium: airTotals.share_total,
          missingData: airTotals.has_missing_data,
          hasWarnings: false,
          warningsTooltip: null,
          overrideType: null,
          stale: airStale,
        },
        rms: {
          aal: rmsTotalAal,
          multiple:
            rmsTotalAal > 0 ? rmsTotals.layer_total / rmsTotalAal : null,
          multipleTooltip: null,
          layerPremium: rmsTotals.layer_total,
          sharePremium: rmsTotals.share_total,
          missingData: rmsTotals.has_missing_data,
          hasWarnings: false,
          warningsTooltip: null,
          overrideType: null,
          stale: rmsStale,
        },
        selectedSource,
        target: layer.target,
        isFirstInGroup: false,
      });
    }
    return rows;
  }, [sortedLayers, enabledLayers, premiumByLayer, resolvedLayerInfo]);

  const tiv = specialtyPropertyInfo.precomputed_metadata?.total_tiv;

  const rateBody = (layerPremium: number) => {
    if (!tiv) return "";
    return (
      <span style={{ fontFamily: "monospace" }}>
        {((layerPremium / tiv) * 100).toFixed(2)}
      </span>
    );
  };

  const sources = [
    ["air", "AIR"],
    ["rms", "RMS"],
  ] as const;

  const overrideBg = isLightMode
    ? HIGHLIGHT_USER_OVERRIDE_LIGHT_MODE
    : HIGHLIGHT_USER_OVERRIDE_DARK_MODE;

  /** Return the background color for warning/missing-data highlights. */
  const warningBg = (data: SourcePremiumData): string | undefined => {
    if (data.hasWarnings)
      return isLightMode
        ? HIGHLIGHT_ERROR_LIGHT_MODE
        : HIGHLIGHT_ERROR_DARK_MODE;
    if (data.missingData)
      return isLightMode
        ? HIGHLIGHT_WARNING_LIGHT_MODE
        : HIGHLIGHT_WARNING_DARK_MODE;
    return undefined;
  };

  const headerColumnGroup = (
    <ColumnGroup>
      <Row>
        <Column header="Layer" rowSpan={2} />
        <Column header="Peril" rowSpan={2} />
        {sources.map(([source, name]) => (
          <Column key={source} header={`Modeled - ${name}`} colSpan={5} />
        ))}
        <Column header="Target" colSpan={2} />
      </Row>
      <Row>
        {sources.flatMap(([source]) => [
          <Column key={`${source}-aal`} header="AAL" />,
          <Column key={`${source}-multiple`} header="Multiple" />,
          <Column key={`${source}-layer-premium`} header="Layer Prem" />,
          <Column key={`${source}-rate`} header="Rate" />,
          <Column key={`${source}-share-premium`} header="Share Prem" />,
        ])}
        <Column header="Premium" />
        <Column header="Rate" />
      </Row>
    </ColumnGroup>
  );

  return (
    <Card title={title || "Layers"}>
      <div className={styles.container}>
        <div className={styles.premiumTable}>
          <div className={styles.premiumTableHeader}>
            <MultiSelect
              value={enabledLayers}
              options={layerOptions}
              onChange={(e) => {
                const newInfo = cloneDeep(
                  specialtyPropertyInfo.report_json.layer_info
                );
                for (const layer of newInfo) {
                  layer.is_enabled = (e.value as string[]).some(
                    (selected) => selected === layer.layer_id
                  );
                }
                updateReport.mutate({ layerInfo: newInfo });
              }}
              placeholder="Select layers"
              style={{ width: "250px" }}
            />
            <Button
              icon="pi pi-plus"
              onClick={openAddDialog}
              tooltip="Add layer"
              text
              raised
              tooltipOptions={{ position: "top" }}
            />
            <Dialog
              header={isEditing ? "Edit Layer" : "Add Layer"}
              visible={showLayerDialog}
              onHide={resetLayerDialog}
              style={{ width: "360px" }}
            >
              <div className={styles.addLayerForm}>
                <label className={styles.addLayerField}>
                  <span>Attachment Point ($)</span>
                  <InputNumber
                    value={newStartingPoint}
                    onChange={(e) => setNewStartingPoint(e.value ?? null)}
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    maxFractionDigits={0}
                    placeholder="0"
                    disabled={isEditing}
                  />
                </label>
                <label className={styles.addLayerField}>
                  <span>Limit ($)</span>
                  <InputNumber
                    value={newRange}
                    onChange={(e) => setNewRange(e.value ?? null)}
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    maxFractionDigits={0}
                    placeholder="0"
                    disabled={isEditing}
                  />
                </label>
                <label className={styles.addLayerField}>
                  <span>Participation ($)</span>
                  <InputNumber
                    value={newParticipation}
                    onChange={(e) => setNewParticipation(e.value ?? null)}
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    maxFractionDigits={0}
                    placeholder="Same as limit"
                  />
                </label>
                {layerPreview && (
                  <div className={styles.layerPreview}>{layerPreview}</div>
                )}
                <Button
                  label={isEditing ? "Save" : "Add"}
                  icon={isEditing ? "pi pi-check" : "pi pi-plus"}
                  onClick={handleSaveLayer}
                  disabled={
                    newStartingPoint == null ||
                    newRange == null ||
                    newRange <= 0 ||
                    updateReport.isPending
                  }
                  loading={updateReport.isPending}
                  style={{ marginTop: "8px" }}
                />
              </div>
            </Dialog>
          </div>
          {firstPremiumLoading ? (
            <div className={styles.loadingContainer}>
              <ProgressSpinner />
            </div>
          ) : premiumError ? (
            <div className={styles.errorContainer}>
              Something went wrong calculating premiums. Let us know which
              report you were working on in #engineering.
            </div>
          ) : (
            // premiumData?.errors?.length ?
            //   <div className={styles.errorContainer}>
            //     <div className={styles.errorTitle}>
            //       Unable to calculate premiums
            //     </div>
            //     <div className={styles.errorDetails}>
            //       {premiumData.errors.map((err, i) => (
            //         <div key={i} className={styles.errorItem}>
            //           <span>{err.message}</span>
            //         </div>
            //       ))}
            //     </div>
            //   </div> :
            <div
              className={`pantheon-small-table ${updateReport.isPending || premiumLoading ? styles.tableStale : ""}`}
            >
              <DataTable<PremiumPerilRow[]>
                value={premiumRows}
                size="small"
                headerColumnGroup={headerColumnGroup}
                rowGroupMode="rowspan"
                groupRowsBy="layerId"
                tableStyle={{ width: "auto" }}
                rowClassName={(rowData: PremiumPerilRow) => {
                  const classes: string[] = [];
                  if (rowData.rowKind === "total") {
                    classes.push(styles.layerTotalRow);
                  }
                  if (rowData.rowKind === "perilCode") {
                    classes.push(styles.perilCodeRow);
                  }
                  if (rowData.isFirstInGroup) {
                    classes.push(styles.layerGroupStart);
                  }
                  return classes.join(" ");
                }}
                editMode="cell"
              >
                <Column
                  field="layerId"
                  style={{ width: "200px" }}
                  bodyClassName={styles.layerCell}
                  body={(rowData: PremiumPerilRow) => {
                    const layer =
                      specialtyPropertyInfo.report_json.layer_info.find(
                        (L) => L.layer_id === rowData.layerId
                      );
                    return (
                      <div>
                        <span className={styles.layerNameRow}>
                          <span>{readableLayer(rowData.layer)}</span>
                          {rowData.isFirstInGroup && layer && (
                            <i
                              className="pi pi-pencil"
                              style={{
                                fontSize: "0.75rem",
                                cursor: "pointer",
                                opacity: 0.6,
                              }}
                              onClick={() => openEditDialog(layer)}
                            />
                          )}
                        </span>
                        {rowData.isFirstInGroup && (
                          <>
                            <div className={styles.sourceRadioGroup}>
                              {sources.map(([source, name]) => (
                                <label
                                  key={source}
                                  className={styles.sourceRadioLabel}
                                >
                                  <RadioButton
                                    name={`source-${rowData.layerId}`}
                                    value={source}
                                    checked={rowData.selectedSource === source}
                                    onChange={() =>
                                      handleSelectedSourceChange(
                                        rowData.layerId,
                                        source
                                      )
                                    }
                                  />
                                  {name}
                                </label>
                              ))}
                            </div>
                            <div className={styles.perilSentence}>
                              {perilSentenceFromEnabledPerils(
                                getEffectiveIncludedPerils(
                                  specialtyPropertyInfo.report_json.policy_info
                                    ?.included_perils ?? [],
                                  premiumData,
                                  rowData.layerId,
                                  rowData.selectedSource
                                ),
                                specialtyPropertyInfo.report_json.policy_info
                                  ?.peril_sentence_override
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  }}
                />
                <Column
                  field="peril"
                  style={{ width: "200px" }}
                  body={(rowData: PremiumPerilRow) => {
                    if (rowData.rowKind === "total") {
                      return <span className={styles.totalPeril}>Total</span>;
                    }
                    if (rowData.rowKind === "perilCode") {
                      return (
                        <span className={styles.perilCodeLabel}>
                          {rowData.perilCodeName}
                        </span>
                      );
                    }
                    return readableNameFromPeril(rowData.peril);
                  }}
                />
                {sources.flatMap(([source]) => [
                  /* AAL column — read-only */
                  <Column
                    key={`${source}-aal`}
                    style={{ width: "100px" }}
                    bodyClassName={(rowData: PremiumPerilRow) =>
                      source !== rowData.selectedSource
                        ? styles.mutedColumn
                        : ""
                    }
                    body={(rowData: PremiumPerilRow) => {
                      const data = rowData[source];
                      const bg = warningBg(data);
                      return (
                        <span
                          className={bg ? styles.highlightPill : undefined}
                          style={{
                            fontFamily: "monospace",
                            backgroundColor: bg,
                          }}
                        >
                          {data.aal > 0
                            ? formatExactMoneyDollars(data.aal, {
                                withRounding: true,
                                withDollarSign: true,
                              })
                            : "-"}
                        </span>
                      );
                    }}
                  />,
                  /* Multiple column — editable */
                  <Column
                    key={`${source}-multiple`}
                    style={{ width: "90px" }}
                    bodyClassName={(rowData: PremiumPerilRow) =>
                      source !== rowData.selectedSource
                        ? styles.mutedColumn
                        : ""
                    }
                    onCellEditComplete={(options) => {
                      const rowData: PremiumPerilRow = options.rowData;
                      if (rowData.rowKind === "perilType") {
                        const newValue = options.newValue as
                          | number
                          | null
                          | undefined;
                        if (newValue === undefined) return;
                        const data = rowData[source];
                        const editorInit = data.multiple;

                        if (
                          newValue == editorInit ||
                          (newValue != null &&
                            editorInit != null &&
                            Math.abs(newValue - editorInit) < 0.001)
                        )
                          return;
                        handleSourceOverrideChange(
                          rowData.layerId,
                          source,
                          rowData.peril,
                          newValue != null ? { multiple: newValue } : undefined
                        );
                      }
                    }}
                    onBeforeCellEditShow={(options) =>
                      options.rowData.rowKind === "perilType"
                    }
                    editor={(options) => {
                      const rowData: PremiumPerilRow = options.rowData;
                      const data = rowData[source];
                      return (
                        <InputNumber
                          value={data.multiple}
                          onValueChange={(e) =>
                            options.editorCallback?.(e.value)
                          }
                          minFractionDigits={1}
                          className={styles.overrideInput}
                          placeholder="-"
                          disabled={updateReport.isPending}
                        />
                      );
                    }}
                    body={(rowData: PremiumPerilRow) => {
                      const data = rowData[source];
                      const isOverridden = data.overrideType === "multiple";
                      const isDerived = data.overrideType === "premium";
                      // Hide derived multiple while override update is pending
                      if (data.stale && !isOverridden) return "-";
                      const multipleText =
                        data.multiple != null ? data.multiple.toFixed(2) : "-";
                      const inner = (
                        <span
                          className={
                            isOverridden ? styles.highlightPill : undefined
                          }
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            fontFamily: "monospace",
                            fontStyle: isDerived ? "italic" : undefined,
                            backgroundColor: isOverridden
                              ? overrideBg
                              : undefined,
                          }}
                        >
                          {multipleText}
                          {isOverridden && (
                            <i
                              className="pi pi-times"
                              style={{
                                fontSize: "0.65rem",
                                cursor: "pointer",
                                opacity: 0.7,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSourceOverrideChange(
                                  rowData.layerId,
                                  source,
                                  rowData.peril as DBPerilType,
                                  undefined
                                );
                              }}
                            />
                          )}
                        </span>
                      );
                      if (!data.multipleTooltip) return inner;
                      return (
                        <TooltipWrapper
                          tooltipText={data.multipleTooltip}
                          showDelay={0}
                          tooltipProps={{
                            pt: {
                              text: {
                                style: {
                                  whiteSpace: "pre",
                                },
                              },
                            },
                          }}
                        >
                          {inner}
                        </TooltipWrapper>
                      );
                    }}
                  />,
                  /* Layer premium column — read-only */
                  <Column
                    key={`${source}-layer-premium`}
                    style={{ width: "110px" }}
                    bodyClassName={(rowData: PremiumPerilRow) =>
                      source !== rowData.selectedSource
                        ? styles.mutedColumn
                        : ""
                    }
                    body={(rowData: PremiumPerilRow) => {
                      const data = rowData[source];
                      if (data.stale) return "-";
                      const isDerived = data.overrideType != null;
                      const bg = warningBg(data);
                      const value = (
                        <span
                          className={bg ? styles.highlightPill : undefined}
                          style={{
                            fontFamily: "monospace",
                            fontStyle: isDerived ? "italic" : undefined,
                            backgroundColor: bg,
                          }}
                        >
                          {formatExactMoneyDollars(data.layerPremium, {
                            withRounding: true,
                            withDollarSign: true,
                          })}
                        </span>
                      );
                      if (!data.warningsTooltip) return value;
                      return (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {value}
                          <TooltipWrapper
                            tooltipText={data.warningsTooltip}
                            showDelay={0}
                          >
                            <i
                              className="pi pi-question-circle"
                              style={{
                                fontSize: "0.75rem",
                                color: "var(--text-color-secondary)",
                                cursor: "help",
                              }}
                            />
                          </TooltipWrapper>
                        </span>
                      );
                    }}
                  />,
                  /* Rate column — read-only */
                  <Column
                    key={`${source}-rate`}
                    style={{ width: "70px" }}
                    bodyClassName={(rowData: PremiumPerilRow) =>
                      source !== rowData.selectedSource
                        ? styles.mutedColumn
                        : ""
                    }
                    body={(rowData: PremiumPerilRow) =>
                      rowData[source].stale
                        ? "-"
                        : rateBody(rowData[source].layerPremium)
                    }
                  />,
                  /* Share premium column — editable */
                  <Column
                    key={`${source}-share-premium`}
                    style={{ width: "110px" }}
                    bodyClassName={(rowData: PremiumPerilRow) =>
                      source !== rowData.selectedSource
                        ? styles.mutedColumn
                        : ""
                    }
                    onCellEditComplete={(options) => {
                      const rowData: PremiumPerilRow = options.rowData;
                      if (rowData.rowKind === "perilType") {
                        const newValue = options.newValue as
                          | number
                          | null
                          | undefined;
                        if (newValue === undefined) return;
                        const data = rowData[source];
                        const editorInit = data.sharePremium;

                        if (newValue == editorInit) return;
                        handleSourceOverrideChange(
                          rowData.layerId,
                          source,
                          rowData.peril,
                          newValue != null ? { premium: newValue } : undefined
                        );
                      }
                    }}
                    onBeforeCellEditShow={(options) =>
                      options.rowData.rowKind === "perilType"
                    }
                    editor={(options) => {
                      const rowData: PremiumPerilRow = options.rowData;
                      const data = rowData[source];
                      return (
                        <InputNumber
                          value={data.sharePremium}
                          onValueChange={(e) =>
                            options.editorCallback?.(e.value)
                          }
                          mode="currency"
                          currency="USD"
                          locale="en-US"
                          maxFractionDigits={0}
                          className={styles.overrideInput}
                          placeholder="-"
                          disabled={updateReport.isPending}
                        />
                      );
                    }}
                    body={(rowData: PremiumPerilRow) => {
                      const data = rowData[source];
                      const isOverridden = data.overrideType === "premium";
                      const isDerived = data.overrideType === "multiple";
                      // Hide derived premium while override update is pending
                      if (data.stale && !isOverridden) return "-";
                      const bg = isOverridden ? overrideBg : warningBg(data);
                      const value = (
                        <span
                          className={bg ? styles.highlightPill : undefined}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            fontFamily: "monospace",
                            fontStyle: isDerived ? "italic" : undefined,
                            backgroundColor: bg,
                          }}
                        >
                          {formatExactMoneyDollars(data.sharePremium, {
                            withRounding: true,
                            withDollarSign: true,
                          })}
                          {isOverridden && (
                            <i
                              className="pi pi-times"
                              style={{
                                fontSize: "0.65rem",
                                cursor: "pointer",
                                opacity: 0.7,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSourceOverrideChange(
                                  rowData.layerId,
                                  source,
                                  rowData.peril as DBPerilType,
                                  undefined
                                );
                              }}
                            />
                          )}
                        </span>
                      );
                      if (!data.warningsTooltip) return value;
                      return (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {value}
                          <TooltipWrapper
                            tooltipText={data.warningsTooltip}
                            showDelay={0}
                          >
                            <i
                              className="pi pi-question-circle"
                              style={{
                                fontSize: "0.75rem",
                                color: "var(--text-color-secondary)",
                                cursor: "help",
                              }}
                            />
                          </TooltipWrapper>
                        </span>
                      );
                    }}
                  />,
                ])}
                <Column
                  field="target"
                  style={{ width: "110px" }}
                  body={(rowData: PremiumPerilRow) =>
                    rowData.target !== null ? (
                      <span style={{ fontFamily: "monospace" }}>
                        {formatExactMoneyDollars(rowData.target, {
                          withRounding: true,
                          withDollarSign: true,
                        })}
                      </span>
                    ) : (
                      ""
                    )
                  }
                />
                <Column
                  style={{ width: "70px" }}
                  body={(rowData: PremiumPerilRow) =>
                    rowData.target !== null ? rateBody(rowData.target) : ""
                  }
                />
              </DataTable>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
